import { fromJS } from 'immutable';
import { createStructuredSelector } from 'reselect';
import { StatusCodes } from 'http-status-codes';

import logger from 'logger';
import routeDefinitions from 'enum/routeDefinitions';
import ApiException from 'services/ApiService/ApiException';
import parseCatch from 'utils/parseCatch';
import makeAction from 'utils/makeAction';
import ApiService from 'services/ApiService/ApiService';

import Api from './Api';


const SET_PERFORMER_BROADCASTS = 'cam4/studio/SET_PERFORMER_BROADCASTS';
const SET_PERFORMER_INFO = 'cam4/studio/SET_PERFORMER_INFO';
const SET_PERFORMER_REVENUE = 'cam4/studio/SET_PERFORMER_REVENUE';

const initialState = fromJS({
  profile: {},
  studio: {},
  revenue: {},
  broadcasts: {},
});

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case SET_PERFORMER_INFO:
      return state.set('profile', fromJS(action.profile));

    case SET_PERFORMER_REVENUE:
      return state.set('revenue', fromJS(action.revenue));

    case SET_PERFORMER_BROADCASTS:
      return state.set('broadcasts', fromJS(action.broadcasts));

    default:
      return state;
  }
}

/**
 * Redux state $key
 *
 * @type {string}
 */
export const NAME = 'performerStudioProfile';

/**
 * Performer profile info
 * @param state
 * @returns {*}
 */
const profile = (state) => state[NAME].get('profile');
/**
 * Studio owner info
 * @param state
 * @returns {*}
 */

/**
 * Performer revenue
 * @param state
 * @returns {*}
 */
const revenue = (state) => state[NAME].get('revenue');

/**
 * Performer broadcasts
 * @param state
 * @returns {*}
 */
const broadcasts = (state) => state[NAME].get('broadcasts');

/**
 * Actions
 * similar with:
 function setProfile(res) {
  return {
    type: SET_PERFORMER_INFO,
    profile: res.data
  };
}
 */
const setProfile = makeAction(SET_PERFORMER_INFO, 'profile');
const setRevenue = makeAction(SET_PERFORMER_REVENUE, 'revenue');
const setBroadcasts = makeAction(SET_PERFORMER_BROADCASTS, 'broadcasts');

/**
 * Call API for all studios belongs to current owner name,
 * and filter results by performerName. Set it to the current state.
 * @param studioOwnerName
 * @param performerName
 * @returns {Function}
 */
function getPerformerStudio(studioOwnerName, performerName) {
  return async(dispatch) => {
    try {
      const url = ApiService.substitute(Api.ROSTER_PERFORMER_URL, { studioOwnerName, performerName });
      const { data: mainInfo } = await ApiService.get(url);

      return dispatch(setProfile(mainInfo));
    } catch (err) {
      if (err instanceof ApiException) {
        if (err.status === StatusCodes.FORBIDDEN) {
          window.location = routeDefinitions.pageNotFound;
        }
        logger.warn(err.message);
        parseCatch(err);
      }

      return undefined;
    }
  };
}

/**
 * Retrieve from the API performer revenues by time frame
 * @param name
 * @returns {Function}
 */
function getPerformerRevenue(performerName, date) {
  return async(dispatch) => {
    try {
      const url = ApiService.substitute(
        Api.PERFORMER_REVENUE_INSIGHTS,
        { performerName, startDate: date[0], endDate: date[1] }
      );
      const { data } = await ApiService.get(url);

      return dispatch(setRevenue(data));
    } catch (e) {
      if (e instanceof ApiException) {
        parseCatch(e);
      }

      return undefined;
    }
  };
}

/**
 * Retreive from the API performer broadcasts by timeframe
 * @param name
 * @returns {Function}
 */
function getPerformerBroadcasts(performerName, date) {
  return async(dispatch) => {
    try {
      const url = ApiService.substitute(
        Api.PERFORMER_HOURS_URL,
        { performerName, startTime: date[0], endTime: date[1] });
      const { data } = await ApiService.get(url);

      return dispatch(setBroadcasts(data.broadcasts));
    } catch (e) {
      if (e instanceof ApiException) {
        parseCatch(e);
      }

      return undefined;
    }
  };
}

export const actionCreators = {
  getPerformerStudio,
  getPerformerRevenue,
  getPerformerBroadcasts,
};

export const selector = createStructuredSelector({
  performerProfile: profile,
  performerRevenue: revenue,
  performerBroadcasts: broadcasts,
});
